import axios from 'axios'
import { useIdpAuth } from '../composables/useIdpAuth'

const billingApi = axios.create({
  baseURL:
    import.meta.env.VITE_APP_LOCALHOST === 'true'
      ? '/api'
      : import.meta.env.VITE_API_URL,
  timeout: 300000, // 5 минут для больших файлов
  withCredentials: true,
  headers: {
    'X-Requested-With': 'XMLHttpRequest'
  }
})

const handleAuthError = error => {
  if (error.response?.status === 401) {
    const { redirectToIdp } = useIdpAuth()

    redirectToIdp('login', window.location.href)
  }

  return Promise.reject(error)
}

billingApi.interceptors.response.use(
  response => {
    if (response?.data?.ok === false) {
      const error = new Error(
        response.data?.data?.message || 'Ошибка запроса'
      )
      error.response = response
      return Promise.reject(error)
    }
    return response
  },
  error => handleAuthError(error)
)

const serializeForecastMRRParams = params => {
  const searchParams = new URLSearchParams()

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return

    if (key === 'filters' && value && typeof value === 'object') {
      Object.entries(value).forEach(([filterKey, filterValue]) => {
        if (filterValue === null || filterValue === undefined || filterValue === '') return

        if (Array.isArray(filterValue)) {
          filterValue.forEach(item => {
            if (item !== null && item !== undefined && item !== '') {
              searchParams.append(`filters[${filterKey}][]`, item)
            }
          })
          return
        }

        searchParams.append(`filters[${filterKey}]`, filterValue)
      })
      return
    }

    searchParams.append(key, value)
  })

  return searchParams
    .toString()
    .replaceAll('%5B', '[')
    .replaceAll('%5D', ']')
}

class ApiService {
  constructor(entityPath) {
    this.entityPath = entityPath
  }

  async getList(params = {}) {
    const response = await billingApi.get(`/${this.entityPath}`, { params })
    return response.data
  }

  async getById(id) {
    const response = await billingApi.get(`/${this.entityPath}/${id}`)
    return response.data
  }

  async getByContractId(contractId, params = {}) {
    const response = await billingApi.get(
      `/${this.entityPath}/contract/${contractId}`,
      { params }
    )
    return response.data
  }

  async getHistoryByProperty(entityId, property, params = {}) {
    const response = await billingApi.get(
      `/${this.entityPath}/entity_id/${entityId}/property/${property}`,
      { params }
    )
    return response.data
  }

  async create(data) {
    const response = await billingApi.post(`/${this.entityPath}`, data)
    return response.data
  }

  async update(id, data) {
    const { id: _, ...dataWithoutId } = data
    const response = await billingApi.patch(
      `/${this.entityPath}/${id}`,
      dataWithoutId
    )
    return response.data
  }

  async delete(id) {
    const response = await billingApi.delete(`/${this.entityPath}/${id}`)
    return response.data
  }

  async download(params = {}) {
    const response = await billingApi.get(`/${this.entityPath}/download`, {
      params,
      responseType: 'blob' // Важно для скачивания файлов
    })
    return response.data
  }

  async copy(ids) {
    const response = await billingApi.post(`/${this.entityPath}/copy`, { ids })
    return response.data
  }
}

export const applicationApi = new ApiService('application')
export const serviceApi = new ApiService('service')
export const agencyApi = new ApiService('agency')
export const positionDecisionMakerApi = new ApiService('position_decision_maker')
export const counterpartyApi = new ApiService('counterparty')
export const contractApi = new ApiService('contract')
export const paymentAccountApi = new ApiService('payment_account')
export const invoiceApi = new ApiService('invoice')
export const paymentAccountHistoryApi = new ApiService('payment_account_history')
export const reasonOfLeavingApi = new ApiService('reason_of_leaving')
export const forecastApi = new ApiService('forecast')

class ForecastCompositionService {
  async getByForecastId(forecastId) {
    const response = await billingApi.get(`/forecast_composition/forecast/${forecastId}`)
    return response.data
  }

  async create(data) {
    const response = await billingApi.post('/forecast_composition', data)
    return response.data
  }

  async update(id, data) {
    const response = await billingApi.patch(`/forecast_composition/${id}`, data)
    return response.data
  }

  async delete(id) {
    const response = await billingApi.delete(`/forecast_composition/${id}`)
    return response.data
  }
}

export const forecastCompositionApi = new ForecastCompositionService()

class UserService extends ApiService {
  constructor() {
    super('user')
  }

  async getAccountManagers() {
    const response = await billingApi.get('/user', {
      params: { role: 'ROLE_ACCOUNT_MANAGER' }
    })
    return response.data
  }

  async getUsersByRole(role, params = {}) {
    const response = await billingApi.get('/user', {
      params: { role, ...params }
    })
    return response.data
  }
}

export const userApi = new UserService()

class DocumentService {
  async uploadDocument(entityId, documentType, file, onProgress = null) {
    const formData = new FormData()
    formData.append('entity_id', entityId)
    formData.append('document_type', documentType)
    formData.append('file', file)

    const response = await billingApi.post('/document', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 600000, // 10 минут для загрузки документов
      onUploadProgress: onProgress
    })
    return response.data
  }

  async uploadActDocument(actId, documentType, file, onProgress = null) {
    const formData = new FormData()
    formData.append('entity_id', actId)
    formData.append('document_type', documentType)
    formData.append('file', file)

    const response = await billingApi.post('/document/act', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      timeout: 600000, // 10 минут для загрузки документов
      onUploadProgress: onProgress
    })
    return response.data
  }

  async getInvoiceDocuments(invoiceId) {
    const response = await billingApi.get(`/document/invoice/${invoiceId}`)
    return response.data
  }

  async getActDocuments(actId) {
    const response = await billingApi.get(`/document/act/${actId}`)
    return response.data
  }

  async getDocuments(entityType, entityId) {
    if (entityType === 'act') {
      return await this.getActDocuments(entityId)
    } else if (entityType === 'invoice') {
      return await this.getInvoiceDocuments(entityId)
    }
    throw new Error('Неизвестный тип сущности')
  }

  async deleteDocument(documentId) {
    const response = await billingApi.delete(`/document/${documentId}`)
    return response.data
  }

  async deleteActDocument(actId, documentId) {
    const response = await billingApi.delete(`/document/act/${actId}/${documentId}`)

    return response.data
  }

  async downloadDocument(documentId) {
    const response = await billingApi.get(`/document/download/${documentId}`, {
      responseType: 'blob',
      headers: {
        Accept: 'application/pdf,application/octet-stream,*/*'
      }
    })

    return response
  }
}

export const documentService = new DocumentService()

class PaymentsService {
  async getPaymentReport(params = {}) {
    const response = await billingApi.get('/analytics/mrr', {
      params
    })

    return response.data
  }

  async getPaymentPeriodReport(params = {}) {
    const response = await billingApi.get('/analytics/payment_period_report', {
      params
    })

    return response.data
  }

  async getPaymentPeriodDocuments(paymentAccountId, paymentPeriod, params = {}) {
    const response = await billingApi.get(
      `/analytics/payment_period_documents/${paymentAccountId}/payment_period/${paymentPeriod}`,
      { params }
    )
    return response.data
  }

  async downloadPaymentReport(params = {}) {
    const response = await billingApi.get('/analytics/mrr/download', {
      params,
      responseType: 'blob'
    })
    return response.data
  }

  async downloadPaymentPeriodReport(params = {}) {
    const response = await billingApi.get(
      '/analytics/payment_period_report/download',
      {
        params,
        responseType: 'blob'
      }
    )
    return response.data
  }

  async getForecastMRR(params = {}) {
    const response = await billingApi.get('/analytics/forecast-mrr', {
      params,
      paramsSerializer: serializeForecastMRRParams
    })

    return response.data
  }

  async downloadForecastMRR(params = {}) {
    const response = await billingApi.get('/analytics/forecast-mrr/download', {
      params,
      paramsSerializer: serializeForecastMRRParams,
      responseType: 'blob'
    })

    return response.data
  }
}

export const paymentsApi = new PaymentsService()

class ManualForecastService {
  async create(data) {
    const response = await billingApi.post('/manual_forecast', data)
    return response.data
  }

  async update(id, data) {
    const response = await billingApi.patch(`/manual_forecast/${id}`, data)
    return response.data
  }

  async delete(id) {
    const response = await billingApi.delete(`/manual_forecast/${id}`)
    return response.data
  }
}

export const manualForecastApi = new ManualForecastService()

const serializeLimitsParams = params => {
  const searchParams = new URLSearchParams()

  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return

    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item !== null && item !== undefined && item !== '') {
          searchParams.append(`${key}[]`, item)
        }
      })
      return
    }

    searchParams.append(key, value)
  })

  return searchParams
    .toString()
    .replaceAll('%5B', '[')
    .replaceAll('%5D', ']')
}

class LimitsService {
  async getSpacesLimits(params = {}) {
    const response = await billingApi.get('/limiter/limits/spaces/', {
      params,
      paramsSerializer: serializeLimitsParams
    })
    return response.data
  }

  async updateSpaceMonthlyLimit(id, data) {
    const response = await billingApi.patch(`/limiter/space-monthly-limit/${id}`, data)
    return response.data
  }

  async getSpaceLimitsManagement(params = {}) {
    const response = await billingApi.get('/limiter/space-limits/', {
      params,
      paramsSerializer: serializeLimitsParams
    })

    return response.data
  }
}

export const limitsApi = new LimitsService()

class InvoiceCompositionService {
  async create(data) {
    const response = await billingApi.post('/invoice_composition', data)
    return response.data
  }

  async getByInvoiceId(invoiceId) {
    const response = await billingApi.get(`/invoice_composition/invoice/${invoiceId}`)

    return response.data
  }

  async update(id, data) {
    const response = await billingApi.patch(`/invoice_composition/${id}`, data)
    return response.data
  }

  async delete(id) {
    const response = await billingApi.delete(`/invoice_composition/${id}`)
    return response.data
  }
}

class AuthService {
  async getMe() {
    const response = await billingApi.get('/auth/me')
    return response.data
  }
}

export const invoiceCompositionService = new InvoiceCompositionService()
export const authApi = new AuthService()

export { ApiService, billingApi }
export default billingApi
